import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import './ScenarioVersionRightPanel.scss';
import {
  InternalApiScenarioContentForValidationLanguageEnum,
  InternalApiScenarioEntityType,
  InternalApiScenarioIntervalTimeUnit,
  InternalApiScenarioLanguage,
  InternalApiScenarioValidationResult,
} from '../../../api-internal';
import {
  SalvButton,
  SalvDropdown,
  SalvFieldHeader,
  SalvSwitch,
  SalvTextField,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import { convertToDropdownOption, formatEnumLabel, FULL_DATE_TIME_FORMAT, pluralize } from '../../../common/helper';
import { useFormikContext } from 'formik';
import { parameterSchema, QueryValidationStatus, ScenarioVersionModalFormValues } from './ScenarioVersionModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faCodeCompare,
  faCircleExclamation,
  faArrowUpRightFromSquare,
  faCircleInfo,
  faTriangleExclamation,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import {
  personInternalApi,
  querytemplateApi,
  scenarioInternalApi,
  scenarioVersionInternalApi,
  transactionInternalApi,
} from '../../../apis';
import { parseErrorMessage } from '../../../common/errors/helpers';
import ParametersBasedInput from './ParametersBasedInput';
import Editor from '../../../common/editor/Editor';
import {
  getComputedParametersCompleter,
  getQueryTemplateCompleter,
  getUserParametersCompleter,
  humanizePeriod,
} from '../../../common/versioning/helper';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '../../../organisation/useMe';
import { getCustomStatementsSQLHighlightingMode } from '../../../common/editor/editor-modes';
import { getValidParameters } from './ParametersEditor';
import InternalLink from '../../../common/InternalLink';
import { generatePath } from 'react-router';
import { InternalNavigation } from '../../../navigation/helper';
import { Ace } from 'ace-builds';
import { Link } from 'react-router-dom';
import { TRANSACTION_LIMIT } from '../../../alerts/AlertTransactions';

export interface ScenarioVersionRightPanelProps {
  scenarioId: string;
  isDisabled: boolean;
  isLibraryScenario: boolean;
  isPeriodic: boolean;
  queryValidationStatus: QueryValidationStatus;
  setQueryValidationStatus: (status: QueryValidationStatus) => void;
  entityType: InternalApiScenarioEntityType;
}

const PeriodicScenarioDescription = ({
  startTime,
  suppressionInterval,
  periodicInterval,
  periodicIntervalTimeUnit,
}: {
  startTime: Dayjs;
  suppressionInterval: number;
  periodicInterval: number;
  periodicIntervalTimeUnit: InternalApiScenarioIntervalTimeUnit;
}) => {
  return (
    <div className='text-s text-secondary m-t-16'>
      <div>
        This is a periodic scenario. The starting time is
        <span className='text-primary text-strong-s'>{' ' + startTime.format(FULL_DATE_TIME_FORMAT)}</span> and it will
        run every
        <span className='text-primary text-strong-s'>
          {' ' + humanizePeriod(periodicInterval, periodicIntervalTimeUnit) + ' '}
        </span>
        after that.
      </div>

      {suppressionInterval !== 0 && (
        <div>
          If an alert has been created due to this scenario, the same entity will not be alerted due to this scenario
          for the next <span className='text-primary text-strong-s'>{suppressionInterval} days</span>.
        </div>
      )}
    </div>
  );
};

const successfulMessage = (result: InternalApiScenarioValidationResult, includeRelatedTransactions: boolean) => (
  <>
    Scenario matched!
    <ul>
      <li>
        <span className='m-x-8'>•</span>Details: {result.details}
      </li>
      {includeRelatedTransactions && result.relatedTransactions && (
        <li>
          <span className='m-x-8'>•</span>
          {result.relatedTransactions.length}
          {result.relatedTransactions.length === TRANSACTION_LIMIT ? '+' : ''} contributing{' '}
          {pluralize(result.relatedTransactions.length, 'transaction', 'transactions')}.
        </li>
      )}
      <li>
        <span className='m-x-8'>•</span>Execution time: {result.executionTime} ms.
      </li>
    </ul>
  </>
);

const ScenarioVersionRightPanel = ({
  scenarioId,
  isPeriodic,
  entityType,
  isDisabled,
  isLibraryScenario,
  queryValidationStatus,
  setQueryValidationStatus,
}: ScenarioVersionRightPanelProps) => {
  const { setFieldValue, setFieldError, values, errors } = useFormikContext<ScenarioVersionModalFormValues>();
  const [queryValidationOnEntityStatus, setQueryValidationOnEntityStatus] = useState<QueryValidationStatus>(
    QueryValidationStatus.NOT_STARTED
  );
  const [entityId, setEntityId] = useState<string>();
  const [entitySearchMessage, setEntitySearchMessage] = useState<string | ReactNode>();

  const currentUser = useMe();

  const { data: queryTemplateNames } = useQuery({
    queryKey: ['query_template_names', currentUser.currentOrganisation?.id],
    queryFn: async () => await querytemplateApi.findAllQueryTemplateNames(),
    initialData: [],
  });

  const { data: computedParameters } = useQuery({
    queryKey: ['computed_parameter_names', currentUser.currentOrganisation?.id, scenarioId],
    queryFn: async () => await scenarioInternalApi.findComputedParameters({ scenarioHandle: scenarioId }),
    initialData: [],
  });

  const { data: entityForValidation, isFetching } = useQuery({
    enabled: !!entityId,
    queryKey: ['entityForValidation', entityId],
    queryFn: () =>
      entityType === InternalApiScenarioEntityType.PERSON
        ? personInternalApi.findPersonByIdInternal({ personId: entityId! })
        : transactionInternalApi.findTransactionById({ transactionId: entityId! }),
    retry: false,
  });

  const textRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (textRef.current) {
      textRef.current.onkeydown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();

          if (entityForValidation) {
            validateOnEntity();
          }
        }
      };
    }
  }, [textRef, entityForValidation]);

  useEffect(() => {
    setQueryValidationStatus(QueryValidationStatus.NOT_STARTED);
  }, [values.query, values.language, values.parameters]);

  const highlightedStatements = useMemo(() => {
    const statements: Ace.HighlightRule[] = [];

    // Matching priority 1
    if (values.parameters.length) {
      const allowedParameterNames = getValidParameters(values.parameters)
        .map((p) => p.name)
        .join('|');

      statements.push({
        token: 'keyword.scenario_parameter',
        regex: `\\$(?:${allowedParameterNames})\\b`,
      });
    }

    // Matching priority 2
    const fixedVariables = computedParameters.map((p) => p.name).join('|');
    statements.push({
      token: 'keyword.fixed_variable',
      regex: `\\$(?:${fixedVariables})\\b`,
    });

    // Matching priority 3
    if (queryTemplateNames.length) {
      const queryTemplateVariables = queryTemplateNames.join('|');
      statements.push({
        token: 'keyword.query_template',
        regex: `\\$(?:${queryTemplateVariables})\\b`,
      });
    }
    return statements;
  }, [values.parameters, queryTemplateNames, computedParameters]);

  const validationStatusMessage = useMemo(() => {
    switch (queryValidationStatus) {
      case QueryValidationStatus.NOT_STARTED:
        return (
          <div className='unvalidated'>
            <FontAwesomeIcon icon={faCircleExclamation} aria-label='Validate' className='m-r-8' />
            Not validated
          </div>
        );

      case QueryValidationStatus.IN_PROGRESS:
        return (
          <div className='in-progress'>
            <FontAwesomeIcon icon={faSpinner} aria-label='In progress' className='m-r-8' />
            Validation in progress
          </div>
        );

      case QueryValidationStatus.FAILED:
        return (
          <div className='failed'>
            <FontAwesomeIcon icon={faCircleXmark} aria-label='Errors' className='m-r-8' />
            {errors.query || 'There was an error with your query'}
          </div>
        );

      default:
        return (
          <div className='passed'>
            <FontAwesomeIcon icon={faCircleCheck} aria-label='Validated' className='m-r-8' />
            No errors found
          </div>
        );
    }
  }, [queryValidationStatus]);
  const validationOnEntityStatusMessage = useMemo(() => {
    switch (queryValidationOnEntityStatus) {
      case QueryValidationStatus.IN_PROGRESS:
        return (
          <div className='in-progress'>
            <FontAwesomeIcon icon={faSpinner} aria-label='In progress' className='m-r-8' />
            Validation in progress
          </div>
        );

      case QueryValidationStatus.FAILED:
        return (
          <div className='failed'>
            <FontAwesomeIcon icon={faCircleXmark} aria-label='Errors' className='m-r-8' />
            {entitySearchMessage || 'There was an error with your query'}
          </div>
        );

      case QueryValidationStatus.PASSED:
        return (
          <div className='passed'>
            <FontAwesomeIcon icon={faCircleCheck} aria-label='Validated' className='m-r-8' />
            {entitySearchMessage || 'No errors found'}
          </div>
        );
    }
  }, [queryValidationOnEntityStatus]);

  const validateQuery = async () => {
    const hasValidParams = values.parameters.reduce((isValid, param, index) => {
      try {
        parameterSchema.validateSync(param);
        setFieldError(`parameters[${index}]`, undefined);
        return isValid;
      } catch (_) {
        setFieldError(`parameters[${index}]`, 'error');
        return false;
      }
    }, true);

    if (hasValidParams) {
      setQueryValidationStatus(QueryValidationStatus.IN_PROGRESS);
      try {
        await scenarioVersionInternalApi.validateScenarioContent({
          scenarioId: scenarioId!,
          internalApiScenarioContentForValidation: {
            content: values.query,
            language: values.language.toString() as InternalApiScenarioContentForValidationLanguageEnum,
            details: values.details,
            logic: values.logic,
            parameters: values.parameters,
            segments: values.segments,
            excludedSegments: values.excludedSegments,
            includeRelatedTransactions: values.includeRelatedTransactions,
          },
        });
        setFieldError('query', undefined);
        setQueryValidationStatus(QueryValidationStatus.PASSED);
      } catch (e: unknown) {
        const message = await parseErrorMessage(e);
        setQueryValidationStatus(QueryValidationStatus.FAILED);
        setFieldError('query', message);
      }
    }
  };

  const validateOnEntity = async () => {
    try {
      setQueryValidationOnEntityStatus(QueryValidationStatus.IN_PROGRESS);
      const validationResult = await scenarioVersionInternalApi.validateScenarioContentAgainstEntity({
        scenarioId: scenarioId!,
        entityId: entityForValidation!.id,
        internalApiScenarioContentForValidation: {
          content: values.query,
          language: values.language.toString() as InternalApiScenarioContentForValidationLanguageEnum,
          details: values.details,
          logic: values.logic,
          parameters: values.parameters,
          segments: values.segments,
          excludedSegments: values.excludedSegments,
          includeRelatedTransactions: values.includeRelatedTransactions,
        },
      });
      const message = validationResult.isMatch
        ? successfulMessage(validationResult, values.includeRelatedTransactions)
        : validationResult.executionTime
          ? `Scenario did not match. Execution time: ${validationResult.executionTime} ms`
          : 'Scenario was skipped based on segments.';
      setEntitySearchMessage(message);
      setQueryValidationOnEntityStatus(QueryValidationStatus.PASSED);
    } catch (e: unknown) {
      const message = await parseErrorMessage(e);
      setEntitySearchMessage(message);
      setQueryValidationOnEntityStatus(QueryValidationStatus.FAILED);
    }
  };

  const includeRelatedTransactionsBlock = () => (
    <>
      <div className='details-editor p-t-24 p-b-8'>
        <SalvFieldHeader
          className='m-b-8'
          size={Size.MEDIUM}
          label={
            <div style={{ display: '-webkit-inline-box' }}>
              <SalvSwitch
                className='m-b-8'
                selected={values.includeRelatedTransactions}
                onChange={(isToggled) => {
                  setFieldValue('includeRelatedTransactions', isToggled);
                }}
                disabled={isDisabled}
              />
              <div className={'m-x-8'}>Show contributing transactions on alert</div>
              <SalvTooltipWrapper
                tooltip={
                  <ul>
                    <li>
                      <span className='m-x-8'>•</span>This option controls whether alerts can display transactions that
                      contributed to the alert being generated.
                    </li>
                    <li>
                      <span className='m-x-8'>•</span>Turning this on does not automatically attach transactions to
                      alerts — the scenario query must be configured to return transaction IDs.
                    </li>
                    <li>
                      <span className='m-x-8'>•</span>Enabling this option is safe: if the query is not configured
                      correctly, alerts will still be created as usual, but without contributing transactions shown.
                    </li>
                    <li>
                      <span className='m-x-8'>•</span>This option is not supported for scenarios using aggregations.
                    </li>
                  </ul>
                }
                placement={'top'}
              >
                <FontAwesomeIcon icon={faCircleInfo} className='cursor-pointer text-secondary' />
              </SalvTooltipWrapper>
            </div>
          }
          description={
            <div>
              Requires the scenario query to return a list of transaction IDs as the {isPeriodic ? '3rd' : '2nd'} return
              value. Read more{' '}
              <Link to='https://help.salv.com/en/articles/523371-contributing-transactions' target='_blank'>
                here
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className='m-l-4' />
              </Link>
            </div>
          }
        />
        {values.includeRelatedTransactions && (
          <div className='warning-message'>
            <FontAwesomeIcon icon={faTriangleExclamation} className='m-l-4' />
            <span>
              <b>Manual configuration required.</b> The scenario SQL query must return transaction IDs as the{' '}
              {isPeriodic ? '3rd' : '2nd'} return value. For details and examples, see the user manual.
            </span>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className='scenario-version-right-panel'>
      <div className='p-b-24'>
        <ParametersBasedInput
          fieldName='logic'
          label={<span className='text-secondary caps-s'>Scenario logic</span>}
          description='Give a description of how your scenario works (use parameters if needed)'
          disabled={isDisabled}
          required={true}
        />
        {isPeriodic && (
          <PeriodicScenarioDescription
            startTime={dayjs(values.startTime!)}
            suppressionInterval={values.suppressionInterval!}
            periodicInterval={values.periodicInterval!}
            periodicIntervalTimeUnit={values.periodicIntervalTimeUnit!}
          />
        )}
      </div>

      <div className='details-editor p-y-24'>
        <ParametersBasedInput
          fieldName='details'
          label='Details'
          description='Define the information that will appear along with the alert generated by this scenario (use parameters if needed)'
          disabled={isDisabled}
          required={true}
        />
      </div>

      {includeRelatedTransactionsBlock()}

      <div className='scenario-query-editor-container'>
        <div>
          <SalvFieldHeader label='Query' size={Size.MEDIUM} />
          <SalvDropdown
            placeholder='Select...'
            disabled={isDisabled}
            options={Object.entries(InternalApiScenarioLanguage)
              .filter(([key]) => !(isPeriodic && key === InternalApiScenarioLanguage.JMESPATH))
              .map(([key, value]) => ({
                key: key,
                value: value,
                label: value,
              }))}
            size={Size.SMALL}
            clearable={false}
            value={convertToDropdownOption(values.language, values.language)}
            className='query-language-field'
            onChange={(option) => {
              setFieldValue('parameters', []);
              setFieldValue('language', option[0].value);
            }}
            ariaLabel='language'
          />
        </div>

        <div data-testid='scenario-query-editor'>
          <Editor
            disabled={isDisabled}
            value={values.query}
            onChange={(query: string) => setFieldValue('query', query)}
            type={values.language}
            syntaxHighlight={!isDisabled}
            enableLiveAutocompletion={true}
            highlightingMode={getCustomStatementsSQLHighlightingMode(highlightedStatements)}
            completers={[
              getUserParametersCompleter(values.parameters),
              getComputedParametersCompleter(computedParameters),
              getQueryTemplateCompleter(queryTemplateNames),
            ]}
            withBorder={true}
            maxLines={values.query.split('\n').length + 2}
          />
        </div>

        {!isDisabled && !isLibraryScenario && (
          <>
            <div className='m-t-24 p-b-24 query-validator'>
              <SalvButton
                variant='muted'
                size={Size.SMALL}
                onClick={validateQuery}
                disabled={queryValidationStatus === QueryValidationStatus.IN_PROGRESS}
                label={
                  <>
                    <FontAwesomeIcon icon={faCodeCompare} aria-label='validate' className='m-r-4' />
                    Validate query and parameters
                  </>
                }
                className='m-r-12'
              />
              <div>{validationStatusMessage}</div>
            </div>
            {!isPeriodic && (
              <>
                <SalvFieldHeader
                  label={`Validate scenario against ${formatEnumLabel(entityType)} (optional)`}
                  size={Size.MEDIUM}
                  className='p-t-24 border-top'
                />
                <div className='query-validator'>
                  <SalvTextField
                    ref={textRef}
                    ariaLabel='find entity'
                    size={Size.SMALL}
                    placeholder={`${formatEnumLabel(entityType)} ID...`}
                    onChange={(query) => {
                      setEntityId(query);
                    }}
                    value={entityId}
                  />
                  <div className='p-l-12'>
                    {entityForValidation && (
                      <div>
                        Found {formatEnumLabel(entityType)}{' '}
                        <InternalLink
                          title={entityForValidation.id}
                          link={generatePath(
                            entityType === InternalApiScenarioEntityType.PERSON
                              ? InternalNavigation.person
                              : InternalNavigation.transaction,
                            { id: entityForValidation.id }
                          )}
                        />
                        .
                      </div>
                    )}
                    {!entityForValidation && !entityId && <div>Enter ID to see a {formatEnumLabel(entityType)}.</div>}
                    {!entityForValidation && entityId && !isFetching && (
                      <div>{formatEnumLabel(entityType)} not found.</div>
                    )}
                  </div>
                </div>
                <div className='query-validator m-t-8'>
                  <SalvButton
                    variant='muted'
                    size={Size.SMALL}
                    onClick={validateOnEntity}
                    disabled={
                      !entityForValidation ||
                      queryValidationOnEntityStatus === QueryValidationStatus.IN_PROGRESS ||
                      isFetching
                    }
                    label={'Run'}
                    className='m-r-12'
                  />
                  {validationOnEntityStatusMessage}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScenarioVersionRightPanel;
